import { useDisclosure } from "@mantine/hooks";
import { useForm, yupResolver } from "@mantine/form";
import { Box, Flex, Group, Stack, useMantineTheme } from "@mantine/core";
import { string, object } from "yup";
import {
  GetOrganizationQuery,
  useUpdateOrganizationMutation,
} from "src/generated/graphql";
import ImageUpload from "src/components/ImageUpload";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import FormCard from "src/components/FormCard";
import {
  CardRadius,
  EMPLOYEE_ROLE_OBJ,
  HeightCard,
  MESSAGES,
} from "src/utils/constants";
import ActionCard from "src/components/ActionCard";
import TaxRate from "./TaxRate";
import useWritePermission from "src/hooks/usePermission";

export interface BasicFormProps {
  organizationData: NonNullable<GetOrganizationQuery["organizationOne"]>;
  refetch: () => void;
}

interface IOrganizatoinFormPayload {
  name: string;
  description: string;
  logoUrl: string | ArrayBuffer | undefined;

  street?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  state?: string;
}

export const organizationDetailsFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .required("Name is required")
    .min(2, "Name must be at least 2 letters")
    .max(30, "Name must not be greater than 30 letters"),
  description: string().nullable().max(200).notRequired(),
  street: string().nullable().max(50),
  zipCode: string().nullable().max(50),
  city: string().nullable().max(50),
  country: string().nullable().max(50),
  state: string().nullable().max(50),
});

export const BasicForm = ({ organizationData }: BasicFormProps) => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgSettings);
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const { name, description, logoUrl, street, zipCode, city, country, state } =
    organizationData || {};
  const form = useForm<IOrganizatoinFormPayload>({
    initialValues: {
      name,
      description,
      logoUrl,
      street: street || "",
      zipCode: zipCode || "",
      city: city || "",
      country: country || "",
      state: state || "",
    } as IOrganizatoinFormPayload,
    validate: yupResolver(organizationDetailsFormSchema),
  });
  const theme = useMantineTheme();

  const setImageFile = (file: string | ArrayBuffer) => {
    form.setFieldValue("logoUrl", file);
  };

  const { value: imageSrcValue, ...profilePicProps } =
    form.getInputProps("logoUrl");

  const isFormUpdated = form.isDirty() && form.isTouched();

  const [updateOrganization, { loading: updateOrgLoading }] =
    useUpdateOrganizationMutation({
      onCompleted: () => {
        toggleFormActive();
        form.resetDirty();
      },
      refetchQueries: ["GetOrganization"],
    });

  const handleSubmit = async (values: IOrganizatoinFormPayload) => {
    const { logoUrl, ...rest } = values;
    let logoImage =
      logoUrl === organizationData.logoUrl ? undefined : (logoUrl as string);
    updateOrganization({
      variables: {
        data: {
          ...rest,
          logoImage,
        },
        id: organizationData.id,
      },
    });
  };

  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={updateOrgLoading}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={() => {
          form.reset();
          toggleFormActive();
        }}
        onEdit={isAllowWrite ? toggleFormActive : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        isFormActive={isFormActive}
      >
        <ActionCard
          title="General"
          height={HeightCard.sm}
          radius={CardRadius.sm}
          size={"sm"}
          headerOnly
        >
          <Group spacing={15} align="start">
            <ImageUpload
              onReadSuccess={setImageFile}
              src={imageSrcValue}
              alt="Organization Image"
              disabled={isFormActive}
              height={"180px"}
              maxWidth={"180px"}
              {...profilePicProps}
            />
            <Stack spacing={10} style={{ flex: 1, minWidth: 250 }}>
              <TextInput
                withAsterisk
                mt="none"
                disabled={isFormActive}
                label="Organization Name"
                {...form.getInputProps("name")}
              />
              <Textarea
                label="Organization Description"
                withAsterisk
                autosize
                minRows={4}
                disabled={isFormActive}
                {...form.getInputProps("description")}
              />
            </Stack>
          </Group>
        </ActionCard>

        <ActionCard
          title="Address"
          mt={"sm"}
          height={HeightCard.sm}
          radius={CardRadius.sm}
          size={"sm"}
          headerOnly
        >
          <Flex align="center" justify="space-between" gap={20}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                mt="none"
                disabled={isFormActive}
                label="Street"
                {...form.getInputProps("street")}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                mt="none"
                disabled={isFormActive}
                label="Zip"
                {...form.getInputProps("zipCode")}
              />
            </Box>
          </Flex>
          <Flex align="center" justify="space-between" gap={20}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                disabled={isFormActive}
                label="City"
                {...form.getInputProps("city")}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                disabled={isFormActive}
                label="Country"
                {...form.getInputProps("country")}
              />
            </Box>
          </Flex>

          <Box sx={{ width: "49%" }}>
            <TextInput
              disabled={isFormActive}
              label="State"
              {...form.getInputProps("state")}
            />
          </Box>
        </ActionCard>
      </FormCard>

      <Box mt={"sm"} mb={"xs"}>
        <TaxRate isAllowWrite={isAllowWrite} />
      </Box>
    </>
  );
};
